import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {NavLink, useHistory} from 'react-router-dom'
import {FormModelType, useForm} from 'reactjs-use-form'

import {Box, Link, TextField} from '@mui/material'
import Typography from '@mui/material/Typography'
import {Theme, useTheme} from '@mui/material/styles'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'
import * as Yup from 'yup'

import signupPngSrc from '@progyny/assets/images/side-banner-white-top.png'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import {numericRegex, PasswordField} from '@progyny/components/PasswordField'
import {SidepanelContainer} from '@progyny/components/SidepanelContainer'
import {
  useSendConfirmRegistrationEmailMutation,
  useSignupMemberMutation,
} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {HASHED_DOMAINS} from '@progyny/utils/constants'
import {isLegacyEmailDomain} from '@progyny/utils/domainHash'

export const useStyles = makeStyles()((theme: Theme) => ({
  formContainer: {
    marginTop: '-56px',
    width: 420,
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
      width: '100%',
      paddingBottom: theme.spacing(14),
    },
    '& h4': {
      fontWeight: 'normal',
    },
  },
  button: {
    margin: `${theme.spacing(4)} 0 0`,
    display: 'block',
    [theme.breakpoints.down('sm')]: {
      margin: 0,
    },
  },
  register: {
    marginBottom: theme.spacing(2.5),
  },
}))

const RequestRegistrationEmail: FC = () => {
  const {classes} = useStyles()
  const history = useHistory()
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const [sendConfirmRegistrationEmail, {loading: loadingEmail}] =
    useSendConfirmRegistrationEmailMutation()
  const [signupEmployeeMutation, {loading: loadingSignup}] =
    useSignupMemberMutation()
  const getErrorMessage = useErrorCodes()
  const {getPath} = useRoutes()

  // RFC 5322 compliant email regex
  const emailValidation = Yup.string()
    .matches(
      /^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
      'Invalid email format'
    )
    .nullable()

  const theme = useTheme()
  const isDownSM = useResponsive('down', 'sm')

  const noAuthLanguage = localStorage.getItem('ui_language') || 'en'

  const formModel: FormModelType = {
    email: {
      value: '',
      required: true,
      validator: (email) => {
        if (!emailValidation.isValidSync(email)) {
          return getErrorMessage('invalid-email')
        } else return ''
      },
    },
    password: {
      value: '',
      required: false,
      validator: (password) => {
        if (password && password.length < 12) {
          return t('profile_security_password_too_short')
        } else if (password && numericRegex.test(password)) {
          return t('profile_security_password_numeric_only')
        } else return ''
      },
    },
  }

  const handleSubmit = async () => {
    const isLegacy = await isLegacyEmailDomain(values.email, HASHED_DOMAINS)

    if (isLegacy && !values.password) {
      enqueueSnackbar({
        message: t('register_request_email_password_is_required'),
        variant: 'error',
      })
      return
    }

    if (isLegacy) {
      signupEmployeeMutation({
        variables: {
          email: values.email,
          password: values.password,
          language: noAuthLanguage,
        },
      })
        .then((res) => {
          const result = res.data?.signupMember
          if (result?.errors) {
            if ((result?.errors[0] as string) === 'no-such-email') {
              const message = t('no_email_exists')
              return enqueueSnackbar({message, variant: 'error'})
            }
            const message = getErrorMessage(result?.errors[0] as string)
            return enqueueSnackbar({message, variant: 'error'})
          }
          return history.push(getPath(RouteEnum.CONFIRM_EMAIL), {
            email: values.email,
          })
        })
        .catch((error) => {
          console.error(error)
        })
    } else {
      sendConfirmRegistrationEmail({
        variables: {email: values.email, language: noAuthLanguage},
      })
        .then((response) => {
          const data = response.data!.sendConfirmRegistrationEmail

          if (data!.success) {
            // redirect to the email-requested page
            return history.push(getPath(RouteEnum.REGISTER_EMAIL_REQUESTED))
          }
          const error = data!.errors![0]
          const message =
            error!.field === '__all__'
              ? error!.messages[0]
              : `${error!.field}: ${error!.messages[0]}`
          enqueueSnackbar({message, variant: 'error'})
        })
        .catch((error) => {
          if (error.networkError && error.networkError.statusCode === 429) {
            enqueueSnackbar({
              message: t('too_many_requests'),
              variant: 'warning',
            })
          } else {
            console.error(error)
          }
        })
    }
  }

  const {
    values,
    errors,
    isDisabled: formIsDisabled,
    handleOnSubmit,
    handleOnChange,
  } = useForm(formModel, handleSubmit)

  const [isLegacyEmail, setIsLegacyEmail] = React.useState(false)

  React.useEffect(() => {
    let isMounted = true

    const checkDomain = async () => {
      const result = await isLegacyEmailDomain(values.email, HASHED_DOMAINS)
      if (isMounted) {
        setIsLegacyEmail(result)
      }
    }

    checkDomain()

    return () => {
      isMounted = false
    }
  }, [values.email])

  const isDisabled = React.useMemo(
    () => formIsDisabled || (isLegacyEmail && !values.password),
    [formIsDisabled, isLegacyEmail, values.password]
  )

  const loading = loadingEmail || loadingSignup
  return (
    <SidepanelContainer
      hideSidePanel={isDownSM}
      styleProps={{
        backgroundPicSrc: signupPngSrc,
        backgroundColor: theme.palette.primary.main,
        logoColor: 'white',
      }}
      mainComponent={
        <Box className={classes.formContainer}>
          <Typography
            variant='h1'
            fontWeight={500}
            className={classes.register}
            textAlign={'center'}
          >
            {t('register_complete_register_to_progyny')}
          </Typography>
          <Box
            whiteSpace={isDownSM ? 'normal' : 'nowrap'}
            fontSize={'inherit'}
            textAlign={'center'}
            mb={4}
          >
            <Typography>
              {t('register_request_email_already_have_account')}{' '}
              <Link component={NavLink} to={getPath(RouteEnum.LOGIN)}>
                {t('register_request_email_please_login')}
              </Link>
            </Typography>
          </Box>
          <form onSubmit={handleOnSubmit}>
            <Box>
              <TextField
                size='small'
                variant='outlined'
                margin='dense'
                label={t('register_request_email_company_email_address')}
                placeholder={t('yourname@yourcompany.com')}
                name='email'
                value={values.email}
                onChange={handleOnChange}
                error={errors?.email.hasError}
                helperText={t(errors?.email.message)}
                fullWidth
                inputProps={{
                  autoComplete: 'email',
                  ...(isDownSM ? {'data-testid': 'email'} : {}),
                }}
              />
            </Box>
            {isLegacyEmail && (
              <Box mt={3}>
                <Box mt={1}>
                  <PasswordField
                    focusInfo
                    showStrength
                    label={t('password_field_password')}
                    onChange={handleOnChange}
                    errors={errors}
                    values={{password: values.password}}
                    placeholder={t('register_complete_choose_strong_password')}
                    required={isLegacyEmail}
                    inputProps={{
                      'data-testid': 'password',
                    }}
                  />
                </Box>
              </Box>
            )}
            <Box
              sx={
                isDownSM
                  ? {
                      position: 'fixed',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'background.default',
                      borderTop: '1px solid',
                      borderColor: 'grey.300',
                      px: 2,
                      pt: 2,
                      pb: 4,
                      zIndex: (theme) => theme.zIndex.appBar,
                    }
                  : undefined
              }
            >
              <SubmitButton
                type='submit'
                loading={loading}
                disabled={isDisabled}
                className={classes.button}
                fullWidth
              >
                {t('common_submit')}
              </SubmitButton>
            </Box>
          </form>
        </Box>
      }
    />
  )
}

export default RequestRegistrationEmail
